<template>
  <section class="app-table-wrap sale-customers-table-wrap">
    <div class="app-table-scroll">
      <table class="app-data-table">
        <thead>
          <tr>
            <th class="app-col-index">#</th>
            <th>
              <button
                type="button"
                class="sale-customers-sort-btn"
                :title="text.detail.customersTab.table.sortTitles.customer"
                @click="emitSort('customer')"
              >
                <span>{{ text.detail.customersTab.table.columns.customer }}</span>
                <i class="fa" :class="sortIconClass('customer')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ text.detail.customersTab.table.columns.mainContact }}</th>
            <th>{{ text.detail.customersTab.table.columns.email }}</th>
            <th class="app-text-center">
              <button
                type="button"
                class="sale-customers-sort-btn sale-customers-sort-btn--center"
                :title="text.detail.customersTab.table.sortTitles.paymentPlans"
                @click="emitSort('plans_total')"
              >
                <span>{{ text.detail.customersTab.table.columns.paymentPlans }}</span>
                <i class="fa" :class="sortIconClass('plans_total')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="app-text-center">
              <button
                type="button"
                class="sale-customers-sort-btn sale-customers-sort-btn--center"
                :title="text.detail.customersTab.table.sortTitles.billed"
                @click="emitSort('billed_total')"
              >
                <span>{{ text.detail.customersTab.table.columns.billed }}</span>
                <i class="fa" :class="sortIconClass('billed_total')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="app-text-center">
              <button
                type="button"
                class="sale-customers-sort-btn sale-customers-sort-btn--center"
                :title="text.detail.customersTab.table.sortTitles.paid"
                @click="emitSort('paid_total')"
              >
                <span>{{ text.detail.customersTab.table.columns.paid }}</span>
                <i class="fa" :class="sortIconClass('paid_total')" aria-hidden="true"></i>
              </button>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="8" class="app-table-empty">{{ text.detail.customersTab.table.states.loading }}</td>
          </tr>

          <tr v-else-if="rows.length === 0">
            <td colspan="8" class="app-table-empty">{{ text.detail.customersTab.table.states.empty }}</td>
          </tr>

          <tr v-else v-for="(row, index) in rows" :key="row.id">
            <td class="app-muted">{{ index + 1 }}</td>
            <td class="app-strong">
              <button
                v-if="Number(row.id || 0) > 0"
                type="button"
                class="sale-customers-link-btn"
                :title="text.detail.customersTab.table.actions.viewPlans"
                @click="$emit('view-detail', row)"
              >
                {{ row.name || '-' }}
              </button>
              <span v-else>{{ row.name || '-' }}</span>
            </td>
            <td>{{ row.main_contact_name || '' }}</td>
            <td>
              <span v-if="row.email">{{ row.email }}</span>
              <span v-else class="app-muted"></span>
            </td>
            <td class="app-text-center app-strong">{{ formatCurrency(row.plans_total ?? row.income_total ?? 0) }}</td>
            <td class="app-text-center app-strong">{{ formatCurrency(row.billed_total) }}</td>
            <td class="app-text-center app-strong">{{ formatCurrency(row.paid_total) }}</td>
            <td class="app-table-actions-cell">
              <div class="app-table-actions-inline">
                <button type="button" class="app-table-icon-btn app-table-icon-btn--view" :title="text.detail.customersTab.table.actions.viewPlans" @click="$emit('view-detail', row)">
                  <i class="fa fa-eye" aria-hidden="true"></i>
                </button>
                <button v-if="canManageCustomers" type="button" class="app-table-icon-btn app-table-icon-btn--delete" :title="text.detail.customersTab.table.actions.delete" @click="$emit('delete-customer', row)">
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>
                <div class="app-table-actions-menu-wrap">
                  <button
                    type="button"
                    class="app-table-icon-btn"
                    :title="text.detail.customersTab.table.actions.more"
                    @click="toggleActionsMenu(row.id, $event)"
                  >
                    <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <teleport to="body">
      <div
        v-if="openRow"
        class="app-table-actions-menu app-table-floating-menu"
        :style="floatingMenuStyle"
      >
        <button
          type="button"
          @click="emitAction('export-pdf', openRow)"
        >
          {{ text.detail.customersTab.table.actions.exportPdf }}
        </button>
        <button
          type="button"
          @click="emitAction('export-excel', openRow)"
        >
          {{ text.detail.customersTab.table.actions.exportExcel }}
        </button>
      </div>
    </teleport>
  </section>
</template>

<script>
import { formatCurrency } from '@/utils/numberFormat';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  computeFloatingMenuPosition,
  resolveToggledMenuId,
} from '@/composables/shared/useFloatingActionsMenu';
import {
  resolveSortIconClass,
  resolveSortTogglePayload,
} from '@/composables/shared/useSorting';

const { catalog: text } = useDomainText('sales');

export default {
  name: 'SaleCustomersTable',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    sortField: {
      type: String,
      default: '',
    },
    sortDirection: {
      type: String,
      default: 'asc',
    },
    canManageCustomers: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['view-detail', 'delete-customer', 'export-pdf', 'export-excel', 'sort-change'],
  data() {
    return {
      text,
      openActionsMenuId: null,
      floatingMenuTop: 0,
      floatingMenuLeft: 0,
    };
  },
  computed: {
    openRow() {
      if (!this.openActionsMenuId) {
        return null;
      }

      return this.rows.find((row) => Number(row?.id || 0) === Number(this.openActionsMenuId)) || null;
    },
    floatingMenuStyle() {
      return {
        position: 'fixed',
        top: `${this.floatingMenuTop}px`,
        left: `${this.floatingMenuLeft}px`,
        zIndex: 1300,
      };
    },
  },
  mounted() {
    document.addEventListener('click', this.handleGlobalClick, true);
    window.addEventListener('resize', this.closeActionsMenu);
    window.addEventListener('scroll', this.closeActionsMenu, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick, true);
    window.removeEventListener('resize', this.closeActionsMenu);
    window.removeEventListener('scroll', this.closeActionsMenu, true);
  },
  methods: {
    sortIconClass(field) {
      return resolveSortIconClass(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );
    },
    emitSort(field) {
      const nextSort = resolveSortTogglePayload(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );

      if (!nextSort) {
        return;
      }

      this.$emit('sort-change', nextSort);
    },
    toggleActionsMenu(rowId, event) {
      if (!event?.currentTarget) {
        return;
      }

      const nextId = resolveToggledMenuId(this.openActionsMenuId, rowId);
      this.openActionsMenuId = nextId;

      if (!nextId) {
        return;
      }

      const position = computeFloatingMenuPosition(event.currentTarget, 230, 6, 8);
      this.floatingMenuTop = position.top;
      this.floatingMenuLeft = position.left;
    },
    closeActionsMenu() {
      this.openActionsMenuId = null;
    },
    handleGlobalClick(event) {
      if (!this.openActionsMenuId) {
        return;
      }

      const target = event?.target;
      if (target instanceof Element && target.closest('.app-table-floating-menu')) {
        return;
      }

      if (target instanceof Element && target.closest('.app-table-actions-menu-wrap')) {
        return;
      }

      this.closeActionsMenu();
    },
    emitAction(action, row) {
      this.closeActionsMenu();

      if (action === 'export-pdf') {
        this.$emit('export-pdf', row);
        return;
      }

      if (action === 'export-excel') {
        this.$emit('export-excel', row);
      }
    },
    formatCurrency(value) {
      return formatCurrency(value);
    },
  },
};
</script>

<style scoped>
.sale-customers-link-btn {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0070cd;
  text-decoration: none;
  cursor: pointer;
  font-weight: 700;
}

.sale-customers-link-btn:hover {
  text-decoration: underline;
}

.sale-customers-sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.sale-customers-sort-btn--center {
  justify-content: center;
  width: 100%;
}
</style>
